import React, { useEffect } from 'react';
import Header from '../common/Header';
import Footer from '../common/Footer';
import { useLocation, Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  const location = useLocation();

  useEffect(() => {
    // Check if there's a hash in the URL
    if (location.hash) {
      // Remove the # from the hash to get the ID
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        // Wait a bit for the page to render and then scroll
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  return (
    <div>
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link to="/" className="inline-block mb-6 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors">
          Back to Contest
        </Link>

        <h1 className="text-3xl font-bold mb-6">Privacy Policy</h1>

        <div className="prose prose-lg">
          <p>Last Updated: December 15, 2024</p>

          <h2 className="text-xl font-semibold mt-6 mb-4">Introduction</h2>
          <p>
            This Privacy Policy describes how we collect, use, and protect your personal information in compliance 
            with applicable privacy laws in Canada, including the <strong>Personal Information Protection and 
            Electronic Documents Act (PIPEDA)</strong> and relevant provincial legislation.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-4">Information We Collect</h2>
          <p>We may collect the following types of personal information:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Personal identification details (e.g., name, email address, phone number)</li>
            <li>Demographic details (e.g., age, residential address)</li>
            <li>Contest participation details and preferences</li>
            <li>Marketing and communication preferences</li>
            <li>Cookies and analytics data (e.g., IP address, browser type, and interactions with our website)</li>
          </ul>
          <p>
            We collect this information directly from you when you sign up for contests, interact with our website, 
            or communicate with us.
          </p>

          <h2 id="cookies" className="text-xl font-semibold mt-6 mb-4 scroll-mt-20">
            Cookies and Similar Technologies
          </h2>
          <p>
            We use cookies and similar tracking technologies to enhance your browsing experience 
            and understand how you interact with our website. You can control your cookie 
            preferences through your browser settings and our cookie consent manager.
          </p>

          <h3 className="text-lg font-semibold mt-4 mb-2">Types of Cookies We Use:</h3>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong>Necessary Cookies:</strong> Essential for the website to function properly. 
              These cannot be disabled.
            </li>
            <li>
              <strong>Analytics Cookies:</strong> Help us understand how visitors interact with our 
              website by collecting and reporting information anonymously.
            </li>
            <li>
              <strong>Marketing Cookies:</strong> Used to track visitors across websites to enable 
              us to display relevant advertisements.
            </li>
            <li>
              <strong>Preference Cookies:</strong> Enable the website to remember your preferences 
              and customize your experience.
            </li>
          </ul>

          <h3 className="text-lg font-semibold mt-4 mb-2">Analytics and Tracking Tools</h3>
          <p>
            We use third-party tools such as Google Analytics and Facebook Pixel to collect and analyze website 
            traffic data, measure advertising effectiveness, and deliver personalized advertisements. These tools 
            may collect information such as:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Your IP address and browser type</li>
            <li>Pages visited and time spent on the website</li>
            <li>Referring websites and links clicked</li>
          </ul>
          <p>
            You can learn more about these tools and manage your preferences using the following links:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <a href="https://tools.google.com/dlpage/gaoptout" className="text-blue-500 hover:underline" target="_blank" rel="noopener noreferrer">
                Google Analytics Opt-out
              </a>
            </li>
            <li>
              <a href="https://www.facebook.com/help/568137493302217" className="text-blue-500 hover:underline" target="_blank" rel="noopener noreferrer">
                Facebook Ad Preferences
              </a>
            </li>
          </ul>

          <h3 className="text-lg font-semibold mt-4 mb-2">Managing Cookies</h3>
          <p>
            You can control your cookie preferences in several ways:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Through our cookie consent banner when you first visit the site</li>
            <li>By clicking the "Cookie Settings" button at the bottom of our website</li>
            <li>Through your browser settings</li>
          </ul>
          <p className="mt-4">
            Please note that blocking some types of cookies may impact your experience of our website 
            and the services we offer.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-4">How We Use Your Information</h2>
          <ul className="list-disc pl-5 space-y-2">
            <li>To administer contests and promotions</li>
            <li>To notify contest participants and winners</li>
            <li>To communicate updates, newsletters, and promotional offers (with your consent)</li>
            <li>To meet legal and regulatory obligations</li>
            <li>To improve our website and services through analytics and tracking tools</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6 mb-4">Sharing Your Information</h2>
          <p>
            We do not sell or rent your personal information. We may share your personal information with:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              Service providers who assist us in administering the contest or providing services (e.g., email
              distribution platforms, data storage providers)
            </li>
            <li>
              Legal or regulatory authorities if required by law or to protect our legal rights
            </li>
            <li>
              Other parties with your explicit consent
            </li>
          </ul>

          <h2 className="text-xl font-semibold mt-6 mb-4">Your Rights</h2>
          <ul className="list-disc pl-5 space-y-2">
            <li>The right to access your personal information</li>
            <li>The right to correct or update inaccurate or incomplete information</li>
            <li>The right to withdraw consent for the use of your information</li>
            <li>The right to request the deletion of your personal information, subject to legal or contractual limitations</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6 mb-4">Contact Us</h2>
          <p>
            For any privacy-related concerns or inquiries, please contact:
          </p>
          <p>
            <strong>Email:</strong> privacy@example.com<br />
            <strong>Mail:</strong> Privacy Officer, [Your Company Name], [Address], Canada
          </p>
        </div>

        <Link to="/" className="inline-block mt-8 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors">
          Back to Contest
        </Link>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
